import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from "react"
import { clearCache } from "src/services/apiCache"
import { FACILITIES, DEFAULT_FACILITY, FACILITY_OPTIONS, isValidFacility } from "@amzn/eclipse-ui-common/constants/facilities"

interface FacilityContextValue {
  facility: string
  facilityName: string
  setFacility: (code: string) => void
}

const STORAGE_KEY = "eclipse_portal_facility"

const FacilityContext = createContext<FacilityContextValue>({
  facility: DEFAULT_FACILITY,
  facilityName: FACILITIES[DEFAULT_FACILITY],
  setFacility: () => {},
})

export const FacilityProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [facility, setFacilityCode] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored && isValidFacility(stored) ? stored : DEFAULT_FACILITY
  })

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, facility)
  }, [facility])

  const setFacility = useCallback((code: string) => {
    // Clears the legacy URL/record apiCache. The TanStack Query cache does not
    // need clearing here because every query key is facility-scoped (see
    // src/queries/queryKeys.ts) — switching facility selects a different key.
    clearCache()
    setFacilityCode(code)
  }, [])

  const facilityName = FACILITIES[facility] ?? facility

  // Memoize so the context value is referentially stable across renders that
  // don't change the facility — avoids re-rendering every consumer needlessly.
  const value = useMemo(
    () => ({ facility, facilityName, setFacility }),
    [facility, facilityName, setFacility],
  )

  return <FacilityContext.Provider value={value}>{children}</FacilityContext.Provider>
}

export const useFacility = () => useContext(FacilityContext)

export { FACILITY_OPTIONS }
